import { useMemo } from "react";
import {
    ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend, ReferenceLine, ReferenceArea,
} from "recharts";
import { Activity, TrendingUp, TrendingDown, AlertTriangle, ShieldCheck } from "lucide-react";

/**
 * Voltage Stability line chart — plots the last 24h of AC output +
 * grid-side voltage against the acceptable ±10 % band, with a secondary
 * axis for grid frequency. Designed for service engineers hunting grid
 * fluctuations, brownouts, sags and swells.
 *
 * Props:
 *   data           API payload from GET /api/plants/{id}/voltage-history
 *   className      optional wrapper classes
 */
export default function VoltageStabilityChart({ data, className = "" }) {
    const {
        samples = [],
        stats = {},
        stability_score,
        excursions = {},
        nominal_ac,
        ac_band_min,
        ac_band_max,
        nominal_frequency,
        freq_band_min,
        freq_band_max,
        device_sn,
        device_model,
        system_type,
        hours,
        sources = {},
    } = data || {};

    // On-grid string inverters don't publish a separate grid-meter voltage —
    // the inverter's AC output (`ACVoltageRUA`) IS the grid tie. In that case
    // we drop the redundant "AC Output V" line and label the single voltage
    // line as "Grid V" so engineers aren't confused by two overlapping traces.
    const gridIsAcOutput = sources.grid_voltage === "ACVoltageRUA";

    // Convert epoch → HH:mm label in IST for the x-axis.
    const rows = useMemo(() => {
        return (samples || []).map((s) => {
            const d = new Date(s.ts * 1000);
            // Convert to IST for display (UTC+5:30).
            const ist = new Date(d.getTime() + 5.5 * 3600 * 1000);
            const hh = String(ist.getUTCHours()).padStart(2, "0");
            const mm = String(ist.getUTCMinutes()).padStart(2, "0");
            return {
                ...s,
                t_label: `${hh}:${mm}`,
                t_full: `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, "0")}-${String(ist.getUTCDate()).padStart(2, "0")} ${hh}:${mm}`,
            };
        });
    }, [samples]);

    // Show a single x-axis tick roughly every 2 hours to keep it readable.
    const tickIndices = useMemo(() => {
        if (rows.length <= 8) return rows.map((_, i) => i);
        const step = Math.max(1, Math.floor(rows.length / 8));
        const idx = [];
        for (let i = 0; i < rows.length; i += step) idx.push(i);
        return idx;
    }, [rows]);

    const tone = useMemo(() => {
        if (stability_score == null) return { color: "text-zinc-500", label: "—", ring: "border-zinc-200" };
        if (stability_score >= 95) return { color: "text-emerald-600", label: "Excellent", ring: "border-emerald-200" };
        if (stability_score >= 85) return { color: "text-emerald-600", label: "Stable", ring: "border-emerald-200" };
        if (stability_score >= 70) return { color: "text-amber-600", label: "Fluctuating", ring: "border-amber-200" };
        return { color: "text-red-600", label: "Unstable", ring: "border-red-200" };
    }, [stability_score]);

    if (!rows.length) {
        return (
            <div className={`bg-white border border-[#E5E7EB] rounded-md p-8 text-center ${className}`} data-testid="voltage-chart-empty">
                <Activity className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
                <div className="text-sm text-zinc-600 font-medium">No voltage samples available</div>
                <div className="text-xs text-zinc-400 mt-1">
                    The inverter hasn't reported voltage telemetry in the last {hours || 24}h — check data-logger connectivity.
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white border border-[#E5E7EB] rounded-md ${className}`} data-testid="voltage-stability-chart">
            {/* Header — title + stability KPI */}
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#F3F4F6]">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Grid Stability</div>
                    <div className="text-base font-semibold text-[#111827] mt-0.5 flex items-center gap-2">
                        Voltage &amp; Frequency · last {hours}h
                        <span className="text-[10px] font-mono text-zinc-400">{device_sn ? `#${device_sn}` : ""}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        {device_model} · {rows.length} samples · IST
                        {system_type && <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-zinc-100 text-[10px] uppercase tracking-widest font-semibold text-zinc-600">{system_type === "hybrid" ? "Hybrid" : "On-Grid"}</span>}
                        {gridIsAcOutput && <span className="ml-2 text-[10px] text-zinc-400">· Grid V sourced from inverter AC output</span>}
                    </div>
                </div>
                {stability_score != null && (
                    <div className={`text-right shrink-0 border-l pl-4 ${tone.ring}`} data-testid="stability-score">
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Stability</div>
                        <div className={`text-2xl font-semibold tabular-nums ${tone.color}`}>
                            {Math.round(stability_score)}<span className="text-sm text-zinc-400">/100</span>
                        </div>
                        <div className={`text-[10px] font-medium ${tone.color}`}>{tone.label}</div>
                    </div>
                )}
            </div>

            {/* Chart */}
            <div className="px-2 pt-4 pb-2">
                <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={rows} margin={{ top: 10, right: 44, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#F3F4F6" vertical={false} />
                        <XAxis
                            dataKey="t_label"
                            ticks={tickIndices.map(i => rows[i]?.t_label).filter(Boolean)}
                            tick={{ fill: "#71717A", fontSize: 11 }}
                            axisLine={{ stroke: "#E5E7EB" }}
                            tickLine={false}
                        />
                        {/* Left axis — Voltage V. Domain widens if there are excursions. */}
                        <YAxis
                            yAxisId="v"
                            domain={[
                                (dataMin) => Math.min(ac_band_min - 5, dataMin - 2),
                                (dataMax) => Math.max(ac_band_max + 5, dataMax + 2),
                            ]}
                            tick={{ fill: "#71717A", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={44}
                            label={{ value: "V", position: "insideTopLeft", offset: -4, fill: "#71717A", fontSize: 10 }}
                        />
                        {/* Right axis — frequency Hz */}
                        <YAxis
                            yAxisId="hz"
                            orientation="right"
                            domain={[49.0, 51.0]}
                            tick={{ fill: "#71717A", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                            label={{ value: "Hz", position: "insideTopRight", offset: -4, fill: "#71717A", fontSize: 10 }}
                        />
                        <Tooltip content={<VoltageTip />} />
                        <Legend
                            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                            iconType="line"
                        />

                        {/* Acceptable voltage band (207–253V for IN 230V nominal) */}
                        <ReferenceArea
                            yAxisId="v"
                            y1={ac_band_min}
                            y2={ac_band_max}
                            strokeOpacity={0}
                            fill="#10B981"
                            fillOpacity={0.05}
                        />
                        {/* Nominal voltage reference line */}
                        <ReferenceLine
                            yAxisId="v"
                            y={nominal_ac}
                            stroke="#10B981"
                            strokeDasharray="4 4"
                            strokeOpacity={0.5}
                            label={{ value: `${nominal_ac}V nominal`, fill: "#059669", fontSize: 10, position: "right" }}
                        />

                        {/* AC output voltage — hidden for on-grid string
                             inverters where AC output IS the grid feed
                             (would otherwise render two overlapping lines). */}
                        {!gridIsAcOutput && (
                            <Line
                                yAxisId="v"
                                type="monotone"
                                dataKey="ac_voltage"
                                name="AC Output V"
                                stroke="#0A0A0A"
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                                isAnimationActive={false}
                            />
                        )}
                        {/* Grid voltage */}
                        <Line
                            yAxisId="v"
                            type="monotone"
                            dataKey="grid_voltage"
                            name="Grid V"
                            stroke="#0EA5E9"
                            strokeWidth={2}
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                        />
                        {/* Grid frequency (right axis) */}
                        <Line
                            yAxisId="hz"
                            type="monotone"
                            dataKey="grid_frequency"
                            name="Grid Hz"
                            stroke="#8B5CF6"
                            strokeWidth={1.5}
                            strokeDasharray="3 3"
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Stats + excursion counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#F3F4F6] border-t border-[#F3F4F6]">
                {!gridIsAcOutput && <StatCell label="AC Output V" stat={stats.ac} unit="V" nominal={nominal_ac} testid="stat-ac" />}
                <StatCell label="Grid V" stat={stats.grid} unit="V" nominal={nominal_ac} testid="stat-grid" />
                <StatCell label="Grid Hz" stat={stats.frequency} unit="Hz" nominal={nominal_frequency} testid="stat-freq" />
                <ExcursionsCell excursions={excursions} totalSamples={rows.length} />
            </div>
        </div>
    );
}

// ─────────────────────────── Tooltip ───────────────────────────

function VoltageTip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
        <div className="bg-[#0B0D11] text-white text-xs px-3 py-2 rounded-sm shadow-lg border border-zinc-800">
            <div className="text-[10px] font-mono text-zinc-400 mb-1">{row.t_full}</div>
            {row.ac_voltage != null && <TipRow label="AC Output" value={`${row.ac_voltage.toFixed(1)} V`} color="#0A0A0A" />}
            {row.grid_voltage != null && <TipRow label="Grid" value={`${row.grid_voltage.toFixed(1)} V`} color="#0EA5E9" />}
            {row.grid_frequency != null && <TipRow label="Frequency" value={`${row.grid_frequency.toFixed(2)} Hz`} color="#8B5CF6" />}
            {row.dc_voltage_pv1 != null && row.dc_voltage_pv1 > 20 && (
                <TipRow label="PV1 DC" value={`${row.dc_voltage_pv1.toFixed(1)} V`} color="#71717A" />
            )}
            {row.battery_voltage != null && row.battery_voltage > 0 && (
                <TipRow label="Battery" value={`${row.battery_voltage.toFixed(2)} V`} color="#71717A" />
            )}
        </div>
    );
}
function TipRow({ label, value, color }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-zinc-400">
                <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {label}
            </span>
            <span className="font-mono">{value}</span>
        </div>
    );
}

// ─────────────────────────── Bottom stat cells ───────────────────────────

function StatCell({ label, stat, unit, nominal, testid }) {
    if (!stat) {
        return (
            <div className="bg-white px-4 py-3" data-testid={testid}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
                <div className="text-sm text-zinc-400 mt-1">No data</div>
            </div>
        );
    }
    const dev = nominal ? Math.abs(stat.avg - nominal) / nominal * 100 : 0;
    const tone = dev > 5 ? "text-amber-600" : "text-zinc-800";
    return (
        <div className="bg-white px-4 py-3" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`text-lg font-semibold tabular-nums mt-0.5 ${tone}`}>
                {stat.latest.toFixed(unit === "Hz" ? 2 : 1)} <span className="text-xs text-zinc-400">{unit}</span>
            </div>
            <div className="text-[10px] text-zinc-500 tabular-nums mt-0.5">
                min <span className="text-zinc-700">{stat.min.toFixed(unit === "Hz" ? 2 : 1)}</span> · max <span className="text-zinc-700">{stat.max.toFixed(unit === "Hz" ? 2 : 1)}</span> · avg <span className="text-zinc-700">{stat.avg.toFixed(unit === "Hz" ? 2 : 1)}</span>
            </div>
        </div>
    );
}

function ExcursionsCell({ excursions, totalSamples }) {
    const total = (excursions.over_voltage || 0) + (excursions.under_voltage || 0) + (excursions.freq_drift || 0);
    const isClean = total === 0;
    return (
        <div className="bg-white px-4 py-3" data-testid="stat-excursions">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Excursions ({totalSamples} samples)</div>
            {isClean ? (
                <div className="text-sm text-emerald-700 font-medium mt-1 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> No incidents
                </div>
            ) : (
                <div className="space-y-0.5 mt-1">
                    {excursions.over_voltage > 0 && (
                        <div className="text-[11px] text-red-600 flex items-center gap-1.5">
                            <TrendingUp className="w-3 h-3" /> Over-voltage · {excursions.over_voltage} sample{excursions.over_voltage === 1 ? "" : "s"}
                        </div>
                    )}
                    {excursions.under_voltage > 0 && (
                        <div className="text-[11px] text-amber-600 flex items-center gap-1.5">
                            <TrendingDown className="w-3 h-3" /> Under-voltage · {excursions.under_voltage} sample{excursions.under_voltage === 1 ? "" : "s"}
                        </div>
                    )}
                    {excursions.freq_drift > 0 && (
                        <div className="text-[11px] text-purple-600 flex items-center gap-1.5">
                            <AlertTriangle className="w-3 h-3" /> Freq. drift · {excursions.freq_drift} sample{excursions.freq_drift === 1 ? "" : "s"}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
